'use client';

import { useEffect, useRef, useState } from 'react';

type Props = {
  value: number;
  durationMs?: number;
  className?: string;
  style?: React.CSSProperties;
  suffix?: string;
  prefix?: string;
};

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

export function AnimatedCounter({
  value,
  durationMs = 1800,
  className = '',
  style,
  suffix = '',
  prefix = '',
}: Props) {
  // Initialize with final value so SSR + initial paint always shows the real number.
  // The animation resets to 0 and counts up when the element enters the viewport.
  const [display, setDisplay] = useState(value);
  const ref = useRef<HTMLSpanElement | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      let raf = 0;
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / durationMs);
        setDisplay(Math.round(value * easeOut(p)));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      setDisplay(0);
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          run();
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, durationMs]);

  return (
    <span ref={ref} className={className} style={style}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}
